import { Component, Input, Output, EventEmitter, forwardRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-tag-input',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './tag-input.component.html',
  styleUrl: './tag-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagInputComponent),
      multi: true
    }
  ]
})
export class TagInputComponent implements ControlValueAccessor {
  @Input() placeholder: string = 'Enter a tag';
  @Input() disabled: boolean = false;
  @Input() styleClass: string = '';
  @Input() maxTags: number | null = null; // Optional max tags limit
  @Input() allowDuplicates: boolean = false; // Allow duplicate tags

  @Output() onChange = new EventEmitter<string[]>();

  tags: string[] = [];
  tagInput: string = '';

  private onChangeCallback = (value: string[]) => {};
  private onTouchedCallback = () => {};

  writeValue(value: string[]): void {
    if (value && Array.isArray(value)) {
      this.tags = [...value];
    } else {
      this.tags = [];
    }
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedCallback = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  addTag(): void {
    const tag = this.sanitizeTagInput(this.tagInput.trim());
    this.tagInput = tag;
    if (!tag || !this.isValidTagValue(tag)) {
      return;
    }
    // Check max tags limit
    if (this.maxTags && this.tags.length >= this.maxTags) {
      return;
    }

    // Check for duplicates if not allowed
    if (!this.allowDuplicates && this.tags.some(t => t.toLowerCase() === tag.toLowerCase())) {
      this.tagInput = '';
      return;
    }

    this.tags.push(tag);
    this.tagInput = '';
    this.updateValue();
    this.onTouchedCallback();
  }

  removeTag(tagToRemove: string): void {
    const index = this.tags.indexOf(tagToRemove);
    if (index > -1) {
      this.tags.splice(index, 1);
      this.updateValue();
      this.onTouchedCallback();
    }
  }

  onTagInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addTag();
      return;
    }
    if (this.disabled || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    if (event.key.length > 1) {
      return;
    }
    if (!/^[a-zA-Z0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onTagInputChange(): void {
    const sanitized = this.sanitizeTagInput(this.tagInput);
    if (sanitized !== this.tagInput) {
      this.tagInput = sanitized;
    }
  }

  onTagInputPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    this.tagInput = this.sanitizeTagInput(this.tagInput + pasted);
  }

  canAddTag(): boolean {
    const tag = this.tagInput.trim();
    return !this.disabled && tag.length > 0 && this.isValidTagValue(tag);
  }

  private sanitizeTagInput(value: string): string {
    return value.replace(/[^a-zA-Z0-9]/g, '');
  }

  private isValidTagValue(tag: string): boolean {
    return /^[a-zA-Z0-9]+$/.test(tag);
  }

  private updateValue(): void {
    this.onChangeCallback([...this.tags]);
    this.onChange.emit([...this.tags]);
  }

  getTags(): string[] {
    return this.tags;
  }

  trackByTag(index: number, tag: string): string {
    return tag;
  }

  isTagDuplicate(tag: string): boolean {
    const input = (this.tagInput || '').trim();
    if (!input) return false;
    return tag.toLowerCase() === input.toLowerCase();
  }

  isInputDuplicate(): boolean {
    const input = (this.tagInput || '').trim();
    if (!input) return false;
    return this.tags.some(t => t.toLowerCase() === input.toLowerCase());
  }
}
